import { useCallback } from 'react';

/**
 * Hook pour nettoyer complètement tous les caches et données de l'application
 */
export const useCacheCleaner = () => {
  
  const clearAllCaches = useCallback(async () => {
    console.log('🧹 Début du nettoyage complet des caches...');
    
    try {
      // 1. Nettoyer localStorage (mais conserver certaines clés de tracking temporairement)
      console.log('🧹 Nettoyage localStorage...');
      
      // Conserver explicitement certains réglages utilisateurs (ne doivent pas être perdus entre connexions)
      // Inclut les variantes par équipe (clé préfixée)
      const preservedValues = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (
          key === 'smtpSettings' ||
          key === 'emailSettings' ||
          key === 'automaticEmailSettings' ||
          key.startsWith('smtpSettings:') ||
          key.startsWith('emailSettings:') ||
          key.startsWith('automaticEmailSettings:')
        ) {
          preservedValues[key] = localStorage.getItem(key);
        }
      }

      // Sauvegarder temporairement les clés de tracking pour éviter des conflits
      const trackingKeys = [
        'lastTechniciansUserKey',
        'lastAbsencesUserKey', 
        'lastOnCallsUserKey',
        'lastSkillsUserKey'
      ];
      const savedTracking = {};
      trackingKeys.forEach(key => {
        savedTracking[key] = localStorage.getItem(key);
      });
      
      // Vider complètement le localStorage
      localStorage.clear();
      
      // Nettoyer aussi les clés de tracking pour forcer un rechargement complet
      trackingKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      // Restaurer les réglages préservés (si présents)
      Object.entries(preservedValues).forEach(([key, value]) => {
        if (value != null) {
          localStorage.setItem(key, value);
        }
      });
      
      // 2. Nettoyer sessionStorage
      console.log('🧹 Nettoyage sessionStorage...');
      sessionStorage.clear();
      
      // 3. Nettoyer les cookies
      console.log('🧹 Nettoyage des cookies...');
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // 4. Nettoyer le cache du navigateur (Service Workers, Cache API)
      if ('caches' in window) {
        console.log('🧹 Nettoyage du cache du navigateur...');
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log('🗑️ Suppression cache:', cacheName);
            return caches.delete(cacheName);
          })
        );
      }
      
      // 5. Nettoyer IndexedDB si présent
      if ('indexedDB' in window) {
        console.log('🧹 Nettoyage IndexedDB...');
        try {
          // Essayer de supprimer les bases de données communes
          const commonDbNames = ['astreintes', 'app', 'react', 'user-data'];
          for (const dbName of commonDbNames) {
            try {
              await new Promise((resolve, reject) => {
                const deleteReq = indexedDB.deleteDatabase(dbName);
                deleteReq.onsuccess = () => resolve();
                deleteReq.onerror = () => resolve(); // Ignorer les erreurs
                deleteReq.onblocked = () => resolve(); // Ignorer si bloqué
              });
            } catch (e) {
              // Ignorer les erreurs de suppression IndexedDB
            }
          }
        } catch (e) {
          console.log('⚠️ Erreur lors du nettoyage IndexedDB (ignorée):', e.message);
        }
      }
      
      // 6. Nettoyer WebSQL si présent (deprecated mais peut exister)
      if ('openDatabase' in window) {
        console.log('🧹 Nettoyage WebSQL...');
        try {
          // WebSQL est deprecated, mais on peut essayer de le nettoyer
          const db = window.openDatabase('', '', '', '');
          if (db) {
            db.transaction(function(tx) {
              tx.executeSql('DROP TABLE IF EXISTS user_data');
              tx.executeSql('DROP TABLE IF EXISTS app_cache');
            });
          }
        } catch (e) {
          // Ignorer les erreurs WebSQL
        }
      }
      
      console.log('✅ Nettoyage complet terminé !');
      
    } catch (error) {
      console.error('❌ Erreur lors du nettoyage des caches:', error);
      // Ne pas lancer d'erreur, juste logger
    }
  }, []);
  
  const forceReload = useCallback(() => {
    console.log('🔄 Rechargement forcé de l\'application...');
    // Utiliser replace pour éviter l'historique
    window.location.replace(window.location.pathname);
  }, []);
  
  const clearAndReload = useCallback(async () => {
    await clearAllCaches();
    // Attendre un peu pour que le nettoyage soit terminé
    setTimeout(() => {
      forceReload();
    }, 100);
  }, [clearAllCaches, forceReload]);
  
  return {
    clearAllCaches,
    forceReload,
    clearAndReload
  };
};

export default useCacheCleaner; 